import Icon, { ExportOutlined } from '@ant-design/icons';
import { Button, Descriptions, Space, Table, Tooltip } from 'antd';
import { ColumnsType } from 'antd/lib/table';
import React, { useCallback, useEffect, useState } from 'react';
import { FormattedDate, FormattedMessage, useIntl } from 'react-intl';
import { Link, useParams } from 'react-router-dom';
import LayoutComponent from '../../../../components/LayoutComponent/LayoutComponent';
import TaskStatusComponent from '../../../../components/TaskStatusComponent/TaskStatusComponent';
import { TaskCopyIssueFile } from '../../../../model/entities';
import { TaskStatusType } from '../../../../model/types';
import { ReactComponent as BackSvg } from '../../../../resources/images/back.svg';
import { ReactComponent as RefreshSvg } from '../../../../resources/images/refresh.svg';
import notificationService from '../../../../services/NotificationService';
import tableService from '../../../../services/TableService';
import styles from './TaskCopyIssueFilePage.module.scss';
import taskCopyIssueFileApi from '../../../../api/TaskCopyIssueFileApi';
import FileSizeComponent from '../../../../components/FileSizeComponent/FileSizeComponent';

/**
 * Returns the task copy issue file page.
 * @returns the task copy issue file page.
 */
const TaskCopyIssueFilePage = (): React.ReactElement => {
    /*** HOOKS ***/

    const intl = useIntl();
    const params = useParams<ParamsType>();
    const [task, setTask] = useState<TaskCopyIssueFile>();
    const [loading, setLoading] = useState<boolean>();

    /*** EFFECTS ***/

    const init = useCallback(async () => {
        try {
            setLoading(true);
            const task = await taskCopyIssueFileApi.get(+params.id);
            setTask(task);
        } catch (error) {
            notificationService.displayError(error, intl);
        } finally {
            setLoading(false);
        }
    }, [intl, params.id]);

    useEffect(() => {
        init();
    }, [init]);

    /*** METHODS ***/

    /*** COMPONENTS ***/

    const items = task ? [task] : [];
    const columns: ColumnsType<TaskCopyIssueFile> = [
        {
            title: <FormattedMessage id="taskCopyIssueFile.projectName" />,
            dataIndex: 'projectName',
            key: 'projectName',
        },
        {
            title: <FormattedMessage id="taskCopyIssueFile.issueTitle" />,
            dataIndex: 'issueIdentifier',
            key: 'issueIdentifier',
            render: (value: string, taskCopyIssueFile: TaskCopyIssueFile) => {
                const projectId = task ? task.projectId?.replace('b.', '') : undefined;
                const url: string = `https://acc.autodesk.eu/docs/issues/projects/${projectId}`;
                return (
                    <a href={url} target="_blank" rel="noreferrer noopener">
                        {value} - {taskCopyIssueFile.issueTitle}
                        <ExportOutlined className={styles.navigationIcon} />
                    </a>
                );
            },
        },
        {
            title: <FormattedMessage id="taskCopyIssueFile.sourceFileName" />,
            dataIndex: 'sourceFileName',
            key: 'sourceFileName',
            sorter: true,
            render: (value: string, taskCopyIssueFile: TaskCopyIssueFile) => {
                const projectId = task ? task.projectId?.replace('b.', '') : undefined;
                const url: string = `https://acc.autodesk.eu/docs/files/projects/${projectId}?folderUrn=${taskCopyIssueFile.sourceFileFolderId}`;
                return (
                    <a href={url} target="_blank" rel="noreferrer noopener">
                        {value}
                        <ExportOutlined className={styles.navigationIcon} />
                    </a>
                );
            },
        },
        {
            title: <FormattedMessage id="taskCopyIssueFile.targetFileName" />,
            dataIndex: 'targetFileName',
            key: 'targetFileName',
            sorter: true,
            render: (value: string, taskCopyIssueFile: TaskCopyIssueFile) => {
                const projectId = task ? task.projectId?.replace('b.', '') : undefined;
                const url: string = `https://acc.autodesk.eu/docs/files/projects/${projectId}?folderUrn=${taskCopyIssueFile.targetFileFolderId}`;
                return (
                    <a href={url} target="_blank" rel="noreferrer noopener">
                        {value}
                        <ExportOutlined className={styles.navigationIcon} />
                    </a>
                );
            },
        },
        {
            title: <FormattedMessage id="taskCopyIssueFile.sourceFileSize" />,
            dataIndex: 'sourceFileSize',
            key: 'sourceFileSize',
            width: 140,
            align: 'right',
            render: (value: number) => <FileSizeComponent value={value} />,
        },
        {
            title: <FormattedMessage id="taskCopyIssueFile.audit.created" />,
            dataIndex: 'auditCreated',
            key: 'created',
            width: 200,
            align: 'center',
            render: (value: string, taskCopyIssueFile: TaskCopyIssueFile) => (
                <FormattedDate
                    value={taskCopyIssueFile.audit!.created as any}
                    day="2-digit"
                    month="2-digit"
                    year="numeric"
                    hour="2-digit"
                    minute="2-digit"
                    second="2-digit"
                />
            ),
        },
        {
            title: <FormattedMessage id="taskCopyIssueFile.audit.updated" />,
            dataIndex: 'auditUpdated',
            key: 'updated',
            width: 200,
            align: 'center',
            render: (value: string, taskCopyIssueFile: TaskCopyIssueFile) => (
                <FormattedDate
                    value={taskCopyIssueFile.audit!.updated as any}
                    day="2-digit"
                    month="2-digit"
                    year="numeric"
                    hour="2-digit"
                    minute="2-digit"
                    second="2-digit"
                />
            ),
        },
        {
            title: <FormattedMessage id="taskCopyIssueFile.status" />,
            dataIndex: 'status',
            key: 'status',
            width: 160,
            align: 'center',
            render: (status: TaskStatusType) => <TaskStatusComponent status={status} />,
        },
        {
            title: <FormattedMessage id="taskCopyIssueFile.targetBimModelIssueStatus" />,
            dataIndex: 'targetBimModelIssueStatus',
            key: 'targetBimModelIssueStatus',
            width: 230,
            align: 'center',
            render: (targetBimModelIssueStatus: TaskStatusType) => (
                <TaskStatusComponent status={targetBimModelIssueStatus} />
            ),
        },
    ];

    return (
        <LayoutComponent pageId="tasks">
            <Descriptions
                title={
                    <>
                        <span className={styles.title}>
                            #{task?.id} - {task?.name}
                        </span>
                        <TaskStatusComponent status={task?.status} />
                        <Space>
                            <Tooltip title={<FormattedMessage id="button.back" />}>
                                <Link to="/tasks">
                                    <Button type="ghost" size="small" icon={<Icon component={BackSvg} />} />
                                </Link>
                            </Tooltip>
                            <Tooltip title={<FormattedMessage id="button.refresh" />}>
                                <Button
                                    type="ghost"
                                    size="small"
                                    icon={<Icon component={RefreshSvg} />}
                                    onClick={init}
                                />
                            </Tooltip>
                        </Space>
                    </>
                }
                column={2}
                size="small"
                className={styles.summary}
            >
                <Descriptions.Item label={<FormattedMessage id="taskCopyIssueFile.projectName" />} span={2}>
                    {task?.projectName}
                </Descriptions.Item>
                <Descriptions.Item label={<FormattedMessage id="task.audit.created" />}>
                    <FormattedDate
                        value={task?.audit?.created as any}
                        day="2-digit"
                        month="2-digit"
                        year="numeric"
                        hour="2-digit"
                        minute="2-digit"
                        second="2-digit"
                    />
                </Descriptions.Item>
                <Descriptions.Item label={<FormattedMessage id="task.audit.updated" />}>
                    <FormattedDate
                        value={task?.audit?.updated as any}
                        day="2-digit"
                        month="2-digit"
                        year="numeric"
                        hour="2-digit"
                        minute="2-digit"
                        second="2-digit"
                    />
                </Descriptions.Item>
                <Descriptions.Item>{task?.description}</Descriptions.Item>
            </Descriptions>
            <Table
                dataSource={items}
                columns={columns}
                pagination={tableService.createMemoryPagination(items, undefined, true)}
                rowKey="id"
                sortDirections={['ascend', 'descend']}
                showSorterTooltip={false}
                loading={loading}
            />
        </LayoutComponent>
    );
};

export default TaskCopyIssueFilePage;

type ParamsType = { id: string };
